import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import { addSpaceGameQuestion } from "../../functions/addSpaceGameQuestion";
import "../addQuestion/qu-add-question-modal.css";
import "./spaceGameSettings.css";

const ANSWER_LETTERS = ["A", "B", "C", "D"];
const DEFAULT_TIMER = "00:30";
const DEFAULT_POINTS = 10;

function secondsToMmSs(totalSeconds) {
  const n = Math.max(0, Number(totalSeconds) || 0);
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function mmSsToSeconds(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  if (!match) return null;
  const minutes = parseInt(match[1], 10);
  const seconds = parseInt(match[2], 10);
  if (seconds > 59) return null;
  return minutes * 60 + seconds;
}

export default function SpaceGameQuestionModal({ isOpen, onClose, onSaved, question }) {
  const adminToken = useSelector(selectAdminToken);
  const [saving, setSaving] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctAnswer, setCorrectAnswer] = useState("C");
  const [timer, setTimer] = useState(DEFAULT_TIMER);
  const [points, setPoints] = useState(DEFAULT_POINTS);

  useEffect(() => {
    if (!isOpen) return;
    if (question) {
      setQuestionText(question.question_text || "");
      setOptions([
        question.option_a || "",
        question.option_b || "",
        question.option_c || "",
        question.option_d || "",
      ]);
      setCorrectAnswer(question.correct_answer || "C");
      setTimer(secondsToMmSs(question.timer_seconds ?? 30));
      setPoints(question.points ?? DEFAULT_POINTS);
    } else {
      setQuestionText("");
      setOptions(["", "", "", ""]);
      setCorrectAnswer("C");
      setTimer(DEFAULT_TIMER);
      setPoints(DEFAULT_POINTS);
    }
  }, [isOpen, question]);

  const handleOptionChange = (idx, value) => {
    setOptions((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      Swal.fire("Error", "Question text is required", "error");
      return;
    }
    if (options.some((opt) => !opt.trim())) {
      Swal.fire("Error", "All four options are required", "error");
      return;
    }
    const timerSeconds = mmSsToSeconds(timer);
    if (timerSeconds === null || timerSeconds <= 0) {
      Swal.fire("Error", "Timer must be a valid mm:ss value", "error");
      return;
    }
    const pointsNum = parseInt(points, 10);
    if (!Number.isFinite(pointsNum) || pointsNum <= 0) {
      Swal.fire("Error", "Points must be a positive number", "error");
      return;
    }

    setSaving(true);
    try {
      await addSpaceGameQuestion({
        id: question?.id,
        questionText: questionText.trim(),
        optionA: options[0].trim(),
        optionB: options[1].trim(),
        optionC: options[2].trim(),
        optionD: options[3].trim(),
        correctAnswer,
        timerSeconds,
        points: pointsNum,
        token: adminToken,
      });
      Swal.fire("Success", question ? "Question updated" : "Question added", "success");
      onSaved();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save question", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="qu-modal-backdrop" onClick={onClose} />
      <div className="qu-modal">
        <h2>{question ? "Edit Question" : "Add New Question"}</h2>

        <div className="qu-form-group">
          <label>Question Text</label>
          <input
            type="text"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter question"
            disabled={saving}
          />
        </div>

        <div className="qu-form-group-v1">
          {options.map((opt, i) => (
            <div className="input-option-holder" key={i}>
              <div className="input-label">{`Option ${ANSWER_LETTERS[i]}`}</div>
              <input
                type="text"
                placeholder={`Option ${ANSWER_LETTERS[i]}`}
                value={opt}
                disabled={saving}
                onChange={(e) => handleOptionChange(i, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="qu-form-group">
          <label>Correct Answer</label>
          <div style={{ display: "flex", gap: "15px", marginTop: "6px" }}>
            {ANSWER_LETTERS.map((letter) => (
              <label key={letter} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input
                  type="radio"
                  name="sgqCorrectAnswer"
                  value={letter}
                  disabled={saving}
                  checked={correctAnswer === letter}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                />
                {letter}
              </label>
            ))}
          </div>
        </div>

        <div className="sgq-row">
          <div className="qu-form-group">
            <label>Question Timer (mm:ss)</label>
            <input
              type="text"
              value={timer}
              placeholder="00:30"
              disabled={saving}
              onChange={(e) => setTimer(e.target.value)}
            />
          </div>
          <div className="qu-form-group">
            <label>Question Points</label>
            <input
              type="number"
              min="1"
              value={points}
              disabled={saving}
              onChange={(e) => setPoints(e.target.value)}
            />
          </div>
        </div>

        <div className="qu-actions">
          <button className="qu-cancel-btn" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          <button className="qu-save-btn" disabled={saving} onClick={handleSubmit}>
            {saving ? "Saving..." : question ? "Update" : "Save Question"}
          </button>
        </div>
      </div>
    </>
  );
}
